import React, { useState } from "react";

interface HelpModalProps {
  onClose: () => void;
}

export function HelpModal({ onClose }: HelpModalProps) {
  const [closeHovered, setCloseHovered] = useState(false);

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Close only if clicking the overlay itself, not the modal content
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>📖 Hướng Dẫn Chơi UNO</h2>
          <button 
            style={{
              ...styles.closeButton,
              ...(closeHovered ? styles.closeButtonHovered : {}),
            }}
            onClick={onClose}
            onMouseEnter={() => setCloseHovered(true)}
            onMouseLeave={() => setCloseHovered(false)}
          >
            ✕
          </button>
        </div>
        
        <div style={styles.content}>
          <section style={styles.section}>
            <h3 style={styles.sectionTitle}>🎯 Mục Tiêu</h3>
            <p style={styles.text}>
              Đặt tất cả các lá bài trong tay bạn xuống trước các đối thủ. Người đầu tiên hết bài sẽ thắng!
            </p>
          </section>

          <section style={styles.section}>
            <h3 style={styles.sectionTitle}>🃏 Cách Chơi</h3>
            <ul style={styles.list}>
              <li>Bạn chỉ có thể đặt bài khi đến lượt của mình</li>
              <li>Bài đặt xuống phải cùng màu, cùng số, hoặc cùng ký hiệu với lá bài trên cùng của chồng bài bỏ</li>
              <li>Nếu không có bài hợp lệ, bạn phải rút thêm 1 lá bài</li>
              <li>Khi còn 1 lá bài, nhớ bấm nút "Call UNO!" để thông báo</li>
            </ul>
          </section>

          <section style={styles.section}>
            <h3 style={styles.sectionTitle}>⚡ Bài Đặc Biệt</h3>
            <ul style={styles.list}>
              <li><strong>Skip (⏭):</strong> Người chơi tiếp theo bị bỏ lượt</li>
              <li><strong>Reverse (🔄):</strong> Đảo ngược chiều chơi</li>
              <li><strong>Draw 2 (+2):</strong> Người chơi tiếp theo phải rút 2 lá bài và bỏ lượt</li>
              <li><strong>Wild (🌈):</strong> Chọn màu bất kỳ để tiếp tục</li>
              <li><strong>Wild Draw 4 (+4):</strong> Chọn màu và người tiếp theo phải rút 4 lá bài</li>
            </ul>
          </section>

          <section style={styles.section}>
            <h3 style={styles.sectionTitle}>⚠️ Luật Quan Trọng</h3>
            <ul style={styles.list}>
              <li>Bạn có thể kéo thả bài để đặt xuống, hoặc click vào bài rồi click vào chồng bài bỏ</li>
              <li>Khi rút bài, bạn có thể đặt lá bài vừa rút xuống ngay nếu hợp lệ</li>
              <li>Nếu quên bấm "Call UNO!" khi còn 1 lá bài, bạn sẽ bị phạt rút thêm 2 lá</li>
              <li>Wild Draw 4 chỉ được dùng khi bạn không có lá bài nào cùng màu với lá bài hiện tại</li>
              <li>Nếu có nhiều lá Draw 2 hoặc Draw 4 liên tiếp, số lá bài phải rút sẽ được cộng dồn</li>
            </ul>
          </section>

          <section style={styles.section}>
            <h3 style={styles.sectionTitle}>💡 Mẹo Chơi</h3>
            <ul style={styles.list}>
              <li>Giữ lại các lá Wild và Wild Draw 4 để dùng vào lúc quan trọng</li>
              <li>Quan sát số lượng bài của đối thủ để đưa ra quyết định</li>
              <li>Sử dụng Reverse và Skip để kiểm soát lượt chơi</li>
              <li>Đừng quên bấm "Call UNO!" khi còn 1 lá bài!</li>
            </ul>
          </section>
        </div>

        <div style={styles.footer}>
          <button style={styles.closeFooterButton} onClick={onClose}>
            Đóng
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0,0,0,0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "20px",
  },
  modal: {
    background: "rgba(30, 30, 50, 0.98)",
    backdropFilter: "blur(10px)",
    borderRadius: "16px",
    maxWidth: "600px",
    width: "100%",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 20px 60px rgba(0,0,0,0.7)",
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.1)",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "24px 30px",
    borderBottom: "2px solid rgba(255,255,255,0.1)",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },
  title: {
    margin: 0,
    color: "white",
    fontSize: "24px",
    fontWeight: "700",
  },
  closeButton: {
    background: "rgba(255,255,255,0.2)",
    border: "none",
    borderRadius: "50%",
    width: "32px",
    height: "32px",
    color: "white",
    fontSize: "20px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.2s",
  },
  closeButtonHovered: {
    background: "rgba(255,255,255,0.3)",
    transform: "scale(1.1)",
  },
  content: {
    padding: "24px 30px",
    overflowY: "auto",
    flex: 1,
    background: "rgba(20, 20, 30, 0.5)",
  },
  section: {
    marginBottom: "24px",
  },
  sectionTitle: {
    margin: "0 0 12px 0",
    color: "#ffffff",
    fontSize: "18px",
    fontWeight: "600",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  text: {
    margin: 0,
    color: "rgba(255,255,255,0.8)",
    fontSize: "15px",
    lineHeight: "1.6",
  },
  list: {
    margin: 0,
    paddingLeft: "20px",
    color: "rgba(255,255,255,0.8)",
    fontSize: "15px",
    lineHeight: "1.8",
  },
  footer: {
    padding: "20px 30px",
    borderTop: "2px solid rgba(255,255,255,0.1)",
    display: "flex",
    justifyContent: "flex-end",
    background: "rgba(20, 20, 30, 0.5)",
  },
  closeFooterButton: {
    padding: "10px 24px",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "600",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
};
